import { useEffect, useState } from "react";
import { supabase } from "../providers/supabase/supabase";

type RoleDataScope = {
  isLoading: boolean;
  isSuperAdmin: boolean;
  isManager: boolean;
  /**
   * Perfil de leitura (migration 184). Vem com isSuperAdmin = true: na tela
   * ele vê "tudo" o que o servidor devolve, e as views leitura_* já devolvem só
   * o recorte do perfil.
   */
  isSomenteLeitura: boolean;
  /**
   * Administrador ou responsável pela unidade (migration 308): a venda conta
   * pela unidade em que foi feita, não pelo vendedor de hoje. A lista de
   * vendas deixa o recorte para a RLS em vez de filtrar pelos vendedores.
   */
  porUnidade: boolean;
  currentAuthUserId: string | null;
  currentCrmUserId: number | null;
  currentCpf: string | null;
  scopedAuthUserIds: string[];
  scopedCrmUserIds: number[];
  scopedCpfs: string[];
};

const toDigits = (value: string | null | undefined) =>
  typeof value === "string" ? value.replace(/\D/g, "") : "";

type Escopo = Omit<RoleDataScope, "isLoading">;

// Uma leitura só para todos os componentes da tela (são ~30 que usam o hook).
// Antes cada montagem refazia /auth/v1/user + 3 RPCs + users em série, e um
// pico de latência do Supabase travava a tela inteira. Papel muda raramente;
// troca de login limpa na hora.
const ESCOPO_TTL_MS = 60_000;
let escopoEmCache: { authUserId: string; valor: Escopo; expiraEm: number } | null = null;
let escopoEmAndamento: Promise<Escopo | null> | null = null;

export const limparEscopoEmCache = () => {
  escopoEmCache = null;
  escopoEmAndamento = null;
};

try {
  supabase.auth.onAuthStateChange?.((evento) => {
    if (evento !== "TOKEN_REFRESHED" && evento !== "INITIAL_SESSION") limparEscopoEmCache();
  });
} catch {
  // Mocks de teste sem auth completo: o cache só expira pelo tempo.
}

const carregarEscopo = async (authUser: { id: string }): Promise<Escopo> => {
  const [{ data: isSuperAdmin }, { data: isManagerRpc }, { data: perfilLeitura }, { data: vejoPorUnidade }] = await Promise.all([
    supabase.rpc("is_super_admin"),
    supabase.rpc("is_manager"),
    supabase.rpc("leitura_perfil"),
    supabase.rpc("vejo_por_unidade" as never),
  ]);
  // Super admin com unidade escolhida (310) vê como o administrador com
  // aquele filtro: escopo pela unidade, não "tudo".
  const naUnidade = !!isSuperAdmin && !perfilLeitura && vejoPorUnidade === true;
  const isManager = (isManagerRpc || naUnidade) && !perfilLeitura;

  // Perfis de leitura leem das views leitura_*, já recortadas no servidor
  // (vendedor, data, sem telefone). Escopo de novo aqui só esvaziaria a
  // tela — o vendedor de teste, por exemplo, espelha outro vendedor.
  if ((isSuperAdmin && !naUnidade) || perfilLeitura) {
    // Still fetch own CRM row so other components can read currentCrmUserId/currentCpf.
    const { data: ownRow } = await supabase
      .from('users')
      .select('id, cpf_cnpj')
      .eq('user_id', authUser.id)
      .maybeSingle();

    return {
      isSuperAdmin: true,
      isManager: false,
      isSomenteLeitura: !!perfilLeitura,
      porUnidade: false,
      currentAuthUserId: authUser.id,
      currentCrmUserId: ownRow?.id ?? null,
      currentCpf: toDigits(ownRow?.cpf_cnpj) || null,
      scopedAuthUserIds: [],
      scopedCrmUserIds: [],
      scopedCpfs: [],
    };
  }

  let { data: ownUserRow } = await supabase
    .from("users")
    .select("id, cpf_cnpj")
    .eq("user_id", authUser.id)
    .maybeSingle();

  // Legacy fallback: if CRM row is missing, provision it via service-role Edge Function.
  if (!ownUserRow) {
    try {
      await supabase.functions.invoke("ensure-crm-user", {
        method: "POST",
      });
    } catch {
      // Fallback for environments where ensure-crm-user is not deployed yet.
      try {
        await supabase.functions.invoke("users", {
          method: "GET",
        });
      } catch {
        // Keep existing behavior if fallback endpoint is unavailable.
      }
    }

    const { data: refetched } = await supabase
      .from("users")
      .select("id, cpf_cnpj")
      .eq("user_id", authUser.id)
      .maybeSingle();
    if (refetched) ownUserRow = refetched;
  }

  if (isManager) {
    const { data: managerScopeUsers, error: managerScopeError } = await supabase
      .rpc("get_manager_scope_users");

    const managerUsers = !managerScopeError && Array.isArray(managerScopeUsers)
      ? managerScopeUsers
      : null;

    const fallbackOwnCpf = toDigits(ownUserRow?.cpf_cnpj);

    return {
      isSuperAdmin: false,
      isManager: true,
      isSomenteLeitura: false,
      porUnidade: vejoPorUnidade === true,
      currentAuthUserId: authUser.id,
      currentCrmUserId: ownUserRow?.id ?? null,
      currentCpf: fallbackOwnCpf || null,
      scopedAuthUserIds: managerUsers
        ? Array.from(new Set(managerUsers.map((user) => user.auth_user_id).filter(Boolean)))
        : [authUser.id],
      scopedCrmUserIds: managerUsers
        ? Array.from(new Set(managerUsers.map((user) => user.crm_user_id).filter((id): id is number => id != null)))
        : ownUserRow?.id ? [ownUserRow.id] : [],
      scopedCpfs: managerUsers
        ? managerUsers
            .map((user) => toDigits(user.cpf_cnpj))
            .filter((cpf) => cpf.length > 0)
        : fallbackOwnCpf ? [fallbackOwnCpf] : [],
    };
  }

  const ownCpf = toDigits(ownUserRow?.cpf_cnpj);

  return {
    isSuperAdmin: false,
    isManager: false,
    isSomenteLeitura: false,
    porUnidade: false,
    currentAuthUserId: authUser.id,
    currentCrmUserId: ownUserRow?.id ?? null,
    currentCpf: ownCpf || null,
    scopedAuthUserIds: [authUser.id],
    scopedCrmUserIds: ownUserRow?.id ? [ownUserRow.id] : [],
    scopedCpfs: ownCpf ? [ownCpf] : [],
  };
};

// Também é a fonte do escopo do dataProvider: uma verificação só por tela.
export const lerEscopoDePapel = async (): Promise<Escopo | null> => {
  // Sessão guardada no navegador: o id basta aqui, e a RLS barra o resto.
  const { data } = await supabase.auth.getSession();
  const authUser = data?.session?.user;
  if (!authUser) return null;
  if (escopoEmCache && escopoEmCache.authUserId === authUser.id && escopoEmCache.expiraEm > Date.now()) {
    return escopoEmCache.valor;
  }
  if (!escopoEmAndamento) {
    escopoEmAndamento = carregarEscopo(authUser)
      .then((valor) => {
        escopoEmCache = { authUserId: authUser.id, valor, expiraEm: Date.now() + ESCOPO_TTL_MS };
        return valor;
      })
      .finally(() => {
        escopoEmAndamento = null;
      });
  }
  return escopoEmAndamento;
};

const ESCOPO_INICIAL: RoleDataScope = {
  isLoading: true,
  isSuperAdmin: false,
  isManager: false,
  isSomenteLeitura: false,
  porUnidade: false,
  currentAuthUserId: null,
  currentCrmUserId: null,
  currentCpf: null,
  scopedAuthUserIds: [],
  scopedCrmUserIds: [],
  scopedCpfs: [],
};

export const useRoleDataScope = (): RoleDataScope => {
  const [scope, setScope] = useState<RoleDataScope>(() =>
    escopoEmCache && escopoEmCache.expiraEm > Date.now()
      ? { ...escopoEmCache.valor, isLoading: false }
      : ESCOPO_INICIAL,
  );

  useEffect(() => {
    let isMounted = true;

    // Hard ceiling so the page can never get stuck on "Carregando...". Any
    // hung edge-function or RPC call past this point falls back to a default
    // (non-super-admin, no scope) instead of blocking the whole UI forever.
    const safetyTimeout = setTimeout(() => {
      if (isMounted) {
        setScope((prev) => (prev.isLoading ? { ...prev, isLoading: false } : prev));
      }
    }, 8000);

    lerEscopoDePapel()
      .then((valor) => {
        if (!isMounted) return;
        setScope((prev) => (valor ? { ...valor, isLoading: false } : { ...prev, isLoading: false }));
      })
      .catch(() => {
        if (isMounted) setScope((prev) => ({ ...prev, isLoading: false }));
      });

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
    };
  }, []);

  return scope;
};
